import { apiClient } from './api';

export interface CalculatedSummary {
  id: number;
  summaryType: string;
  year: number;
  month?: number;
  referenceId?: number;
  referenceName?: string;
  calculatedData: Record<string, any>;
  metadata?: Record<string, any>;
  calculatedAt: string;
}

export interface HRCostSummary {
  totalBaseSalary: number;
  totalInsuranceRetirement: number;
  totalCompanyBurden: number;
  totalMonthlyBurden: number;
  totalBonusAmount: number;
  totalWelfareCost: number;
  totalFixedLaborCost: number;
  totalMonthlyLaborCost: number;
  totalOpexAllocation: number;
  totalEps: number;
  totalMonthlyEps: number;
  totalEcm: number;
  totalFinalLaborCost: number;
  employeeCount: number;
}

export interface OpexSummary {
  indirectTotal: number;
  directTotal: number;
  monthTotal: number;
  employeeCount: number;
  confirmed: boolean;
}

export interface OpexAnnualSummary {
  annualIndirectTotal: number;
  annualDirectTotal: number;
  annualTotal: number;
}

export interface CREmployeeSummary {
  totalCR: number;
  projectCount: number;
  totalRevenue: number;
  totalCost: number;
}

class CalculatedSummaryApi {
  /**
   * 수동으로 특정 연도의 모든 계산 요약 업데이트
   */
  async updateByYear(year: number): Promise<{ updated: string[]; message: string }> {
    const response = await apiClient.post<{ updated: string[]; message: string }>(`/calculated-summary/update/${year}`, {});
    return response;
  }

  /**
   * HR Cost 월별 요약 데이터 조회 (전체 년도)
   */
  async getHRCostSummaries(year: number): Promise<CalculatedSummary[]> {
    const response = await apiClient.get<CalculatedSummary[]>(`/calculated-summary/hr-cost/${year}`);
    return response;
  }

  /**
   * HR Cost 특정 월 요약 데이터 조회
   */
  async getHRCostMonthlySummary(year: number, month: number): Promise<CalculatedSummary | null> {
    const response = await apiClient.get<CalculatedSummary | null>(`/calculated-summary/hr-cost/${year}/${month}`);
    return response;
  }

  /**
   * HR Cost 연도별 요약 데이터 조회
   */
  async getHRCostYearlySummary(year: number): Promise<CalculatedSummary | null> {
    const response = await apiClient.get<CalculatedSummary | null>(`/calculated-summary/hr-cost/${year}`);
    return response;
  }

  /**
   * OPEX 요약 데이터 조회 (월별 + 연간)
   */
  async getOpexSummaries(year: number): Promise<{
    monthly: CalculatedSummary[];
    annual: CalculatedSummary | null;
  }> {
    const response = await apiClient.get<{
      monthly: CalculatedSummary[];
      annual: CalculatedSummary | null;
    }>(`/calculated-summary/opex/${year}`);
    return response;
  }

  /**
   * OPEX 특정 월 요약 데이터 조회
   */
  async getOpexMonthlySummary(year: number, month: number): Promise<CalculatedSummary | null> {
    const response = await apiClient.get<CalculatedSummary | null>(`/calculated-summary/opex/${year}/${month}`);
    return response;
  }

  /**
   * CR 요약 데이터 조회 (직원별 + 전체)
   */
  async getCRSummaries(year: number): Promise<{
    employees: CalculatedSummary[];
    total: CalculatedSummary | null;
  }> {
    const response = await apiClient.get<{
      employees: CalculatedSummary[];
      total: CalculatedSummary | null;
    }>(`/calculated-summary/cr/${year}`);
    return response;
  }

  /**
   * 특정 타입의 계산 요약 데이터 조회
   */
  async getCalculatedSummaries(
    summaryType: string,
    year: number,
    month?: number
  ): Promise<CalculatedSummary[]> {
    let url = `/calculated-summary/${summaryType}/${year}`;
    if (month !== undefined) {
      url += `?month=${month}`;
    }
    const response = await apiClient.get<CalculatedSummary[]>(url);
    return response;
  }

  /**
   * 특정 계산 요약 데이터 조회
   */
  async getCalculatedSummary(
    summaryType: string,
    year: number,
    month: number,
    referenceId?: number
  ): Promise<CalculatedSummary | null> {
    let url = `/calculated-summary/${summaryType}/${year}/${month}`;
    if (referenceId !== undefined) {
      url += `?referenceId=${referenceId}`;
    }
    const response = await apiClient.get<CalculatedSummary | null>(url);
    return response;
  }
}

export const calculatedSummaryApi = new CalculatedSummaryApi();
export default calculatedSummaryApi;